import { useEffect, useState, useCallback } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import MatchResultDialog from "../components/MatchResultDialog";
import MatchReportDialog from "../components/convocations/MatchReportDialog";
import ConvocationFormDialog from "../components/convocations/ConvocationFormDialog";
import ConvocationListItem from "../components/convocations/ConvocationListItem";
import PlayerRatingsDialog from "../components/convocations/PlayerRatingsDialog";
import { exportConvocationPdf } from "../components/convocations/convocationPdf";
import { exportDistintaPdf } from "../components/convocations/distintaGaraPdf";

const empty = {
  title: "",
  match_date: "",
  meeting_time: "",
  meeting_place: "",
  location: "",
  field_id: "",
  opponent: "",
  notes: "",
  manifestazione: "",
  girone: "",
  home_or_away: "home",
  player_ids: [],
  accompagnatori_ids: [],
};

export default function ConvocationsPage() {
  const { activeTeam, canEdit, season } = useTeams();
  const [items, setItems] = useState([]);
  const [players, setPlayers] = useState([]);
  const [fields, setFields] = useState([]);
  const [staff, setStaff] = useState([]);
  const [parents, setParents] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [resultOpen, setResultOpen] = useState(false);
  const [resultConv, setResultConv] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportConv, setReportConv] = useState(null);
  const [ratingsOpen, setRatingsOpen] = useState(false);
  const [ratingsConv, setRatingsConv] = useState(null);

  const load = useCallback(async () => {
    if (!activeTeam) return;
    const [c, p, f, s, pa] = await Promise.all([
      api.get(`/teams/${activeTeam.id}/convocations`, { params: { season } }),
      api.get(`/teams/${activeTeam.id}/players`),
      api.get(`/teams/${activeTeam.id}/fields`),
      api.get(`/teams/${activeTeam.id}/staff`),
      api.get(`/teams/${activeTeam.id}/parents`).catch(() => ({ data: [] })),
    ]);
    setItems(c.data);
    setPlayers(p.data);
    setFields(f.data);
    setStaff(s.data);
    setParents(pa.data || []);
  }, [activeTeam, season]);

  useEffect(() => {
    load();
  }, [load]);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;

  const submit = async () => {
    if (!form.title || !form.match_date) {
      toast.error("Titolo e data richiesti");
      return;
    }
    const payload = { ...form, team_id: activeTeam.id, season };
    if (editingId) await api.put(`/convocations/${editingId}`, payload);
    else await api.post(`/convocations`, payload);
    toast.success("Convocazione salvata");
    setFormOpen(false);
    setForm(empty);
    setEditingId(null);
    load();
  };

  const handleNew = () => {
    setForm(empty);
    setEditingId(null);
    setFormOpen(true);
  };

  const handleEdit = (c) => {
    setForm({ ...empty, ...c });
    setEditingId(c.id);
    setFormOpen(true);
  };

  const handleDelete = async (c) => {
    if (!window.confirm("Eliminare la convocazione?")) return;
    await api.delete(`/convocations/${c.id}`);
    load();
  };

  const handleExportPdf = (c) => exportConvocationPdf(c, players, activeTeam);

  const handleExportDistinta = (c) => {
    const field = c.field_id ? fields.find((f) => f.id === c.field_id) : null;
    exportDistintaPdf(c, players, activeTeam, field, staff, parents);
  };

  const handleOpenResult = (c) => {
    setResultConv(c);
    setResultOpen(true);
  };

  const handleOpenReport = (c) => {
    setReportConv(c);
    setReportOpen(true);
  };

  const handleOpenRatings = (c) => {
    setRatingsConv(c);
    setRatingsOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="convocations-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Convocazioni</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Distinte gara</h1>
          <p className="text-slate-500">{items.length} convocazioni</p>
        </div>
        <Button
          onClick={handleNew}
          className="rounded-full bg-sky-500 hover:bg-sky-600"
          data-testid="new-convocation-btn"
          style={!canEdit ? { display: "none" } : {}}
        >
          <Plus size={16} className="mr-1" /> Nuova
        </Button>
      </div>

      <div className="space-y-3">
        {items.map((c) => (
          <ConvocationListItem
            key={c.id}
            convocation={c}
            canEdit={canEdit}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onExportPdf={handleExportPdf}
            onExportDistinta={handleExportDistinta}
            onOpenResult={handleOpenResult}
            onOpenReport={handleOpenReport}
            onOpenRatings={handleOpenRatings}
            players={players}
            team={activeTeam}
            fields={fields}
          />
        ))}
        {items.length === 0 && (
          <Card className="p-10 text-center text-slate-500 border-dashed">
            Nessuna convocazione ancora.
          </Card>
        )}
      </div>

      <ConvocationFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        form={form}
        setForm={setForm}
        players={players}
        fields={fields}
        staff={staff}
        parents={parents}
        editingId={editingId}
        onSubmit={submit}
      />

      <MatchResultDialog
        open={resultOpen}
        onOpenChange={setResultOpen}
        convocation={resultConv}
        players={players}
        onSaved={load}
      />

      <MatchReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        convocation={reportConv}
        players={players}
        onSaved={load}
      />

      <PlayerRatingsDialog
        open={ratingsOpen}
        onOpenChange={setRatingsOpen}
        convocation={ratingsConv}
        players={players}
        onSaved={load}
      />
    </div>
  );
}
